import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  GraduationCap,
  Users,
  Building2,
  Calendar,
  Lock,
  Info,
  RefreshCw
} from 'lucide-react';
import {
  EventCategory,
  CreateEventCategoryDto,
  UpdateEventCategoryDto,
  EventVisibility,
  VISIBILITY_LABELS,
  VISIBILITY_DESCRIPTIONS,
  annualPlanningService,
} from '../../services/annualPlanningService';
import { ConfirmModal } from '../annual-planning/ConfirmModal';

// Available icons for categories
const ICON_OPTIONS = [
  { name: 'GraduationCap', icon: GraduationCap, label: 'Académique' },
  { name: 'Users', icon: Users, label: 'Groupe' },
  { name: 'Building2', icon: Building2, label: 'Administratif' },
  { name: 'Calendar', icon: Calendar, label: 'Calendrier' },
];

// Color palette for categories
const COLOR_OPTIONS = [
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#EC4899', // Pink
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#F97316', // Orange
  '#6B7280', // Gray
];

interface CategoryFormData {
  name: string;
  code: string;
  description: string;
  color: string;
  icon: string;
  visibility: EventVisibility;
}

const initialFormData: CategoryFormData = {
  name: '',
  code: '',
  description: '',
  color: '#3B82F6',
  icon: 'Calendar',
  visibility: 'ALL',
};

interface EventCategoriesSettingsProps {
  canManage?: boolean;
}

export const EventCategoriesSettings: React.FC<EventCategoriesSettingsProps> = ({ canManage = true }) => {
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<EventCategory | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>(initialFormData);
  const [categoryToDelete, setCategoryToDelete] = useState<EventCategory | null>(null);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const data = await annualPlanningService.getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const getIconComponent = (iconName?: string) => {
    const iconOption = ICON_OPTIONS.find(opt => opt.name === iconName);
    return iconOption ? iconOption.icon : Calendar;
  };

  const handleEdit = (category: EventCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      code: category.code,
      description: category.description || '',
      color: category.color,
      icon: category.icon || 'Calendar',
      visibility: category.visibility,
    });
    setShowForm(true);
    setError(null);
  };

  const handleCreate = () => {
    setEditingCategory(null);
    setFormData(initialFormData);
    setShowForm(true);
    setError(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCategory(null);
    setFormData(initialFormData);
    setError(null);
  };

  const generateCode = (name: string): string => {
    return name
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^A-Z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .substring(0, 20);
  };

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      // Auto-generate code only if creating new or code hasn't been manually edited
      code: !editingCategory ? generateCode(name) : prev.code,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Le nom de la catégorie est requis');
      return;
    }

    if (!formData.code.trim()) {
      setError('Le code de la catégorie est requis');
      return;
    }

    try {
      setSaving(true);

      if (editingCategory) {
        // For SYSTEM categories, only color is allowed to be updated
        // For CUSTOM categories, all fields can be updated
        const updateData: UpdateEventCategoryDto = editingCategory.categoryType === 'SYSTEM'
          ? {
              color: formData.color,
            }
          : {
              name: formData.name,
              description: formData.description || undefined,
              color: formData.color,
              icon: formData.icon,
              visibility: formData.visibility,
            };
        await annualPlanningService.updateCategory(editingCategory.id, updateData);
      } else {
        const createData: CreateEventCategoryDto = {
          name: formData.name,
          code: formData.code,
          description: formData.description || undefined,
          color: formData.color,
          icon: formData.icon,
          visibility: formData.visibility,
        };
        await annualPlanningService.createCategory(createData);
      }

      handleCancel();
      loadCategories();
    } catch (err: unknown) {
      console.error('Failed to save category:', err);
      if (err && typeof err === 'object' && 'message' in err) {
        setError((err as { message: string }).message);
      } else {
        setError('Une erreur est survenue lors de l\'enregistrement');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (category: EventCategory) => {
    if (category.categoryType === 'SYSTEM') {
      setError('Les catégories système ne peuvent pas être supprimées');
      return;
    }
    setCategoryToDelete(category);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    try {
      setSaving(true);
      await annualPlanningService.deleteCategory(categoryToDelete.id);
      setCategoryToDelete(null);
      loadCategories();
    } catch (err) {
      console.error('Failed to delete category:', err);
      setError('Erreur lors de la suppression de la catégorie');
      setCategoryToDelete(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCancel = () => {
    setCategoryToDelete(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Chargement des catégories...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Catégories d'événements
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Gérez les catégories d'événements et leurs règles de visibilité
          </p>
        </div>
        {!showForm && canManage && (
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={18} />
            Nouvelle catégorie
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {showForm ? (
        /* Category Form */
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">
            {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            {editingCategory?.categoryType === 'SYSTEM' && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                <Lock size={16} />
                <span>Catégorie système - seule la couleur peut être modifiée</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la catégorie *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="ex: Formation Continue"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  disabled={editingCategory?.categoryType === 'SYSTEM'}
                />
              </div>

              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="ex: FORMATION_CONTINUE"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                  required
                  disabled={!!editingCategory}
                />
                {editingCategory && (
                  <p className="text-xs text-gray-500 mt-1">Le code ne peut pas être modifié</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description de la catégorie..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                disabled={editingCategory?.categoryType === 'SYSTEM'}
              />
              {editingCategory?.categoryType === 'SYSTEM' && (
                <p className="text-xs text-gray-500 mt-1">Les catégories système ne peuvent pas être modifiées</p>
              )}
            </div>

            {/* Icon Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Icône
              </label>
              <div className="flex gap-2">
                {ICON_OPTIONS.map(({ name, icon: Icon, label }) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: name })}
                    className={`
                      p-3 rounded-lg border-2 transition-all
                      ${formData.icon === name
                        ? 'border-primary-500 bg-primary-50 text-primary-600'
                        : 'border-gray-200 hover:border-gray-300 text-gray-500'
                      }
                    `}
                    title={label}
                  >
                    <Icon size={20} />
                  </button>
                ))}
              </div>
            </div>

            {/* Color Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Couleur
              </label>
              <div className="flex gap-2 flex-wrap">
                {COLOR_OPTIONS.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    className={`
                      w-8 h-8 rounded-full border-2 transition-all
                      ${formData.color === color
                        ? 'border-gray-900 scale-110'
                        : 'border-transparent hover:scale-105'
                      }
                    `}
                    style={{ backgroundColor: color }}
                  />
                ))}
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-8 h-8 rounded-full cursor-pointer"
                  title="Couleur personnalisée"
                />
              </div>
            </div>

            {/* Visibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Visibilité des événements
              </label>
              <div className="space-y-2">
                {(Object.keys(VISIBILITY_LABELS) as EventVisibility[]).map(visibility => (
                  <label
                    key={visibility}
                    className={`
                      flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                      ${formData.visibility === visibility
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="visibility"
                      value={visibility}
                      checked={formData.visibility === visibility}
                      onChange={(e) => setFormData({ ...formData, visibility: e.target.value as EventVisibility })}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-gray-900">
                        {VISIBILITY_LABELS[visibility]}
                      </div>
                      <div className="text-sm text-gray-500">
                        {VISIBILITY_DESCRIPTIONS[visibility]}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Enregistrement...' : editingCategory ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        /* Category List */
        <div className="space-y-3">
          {categories.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
              <Calendar size={48} className="mx-auto mb-4 opacity-50" />
              <p>Aucune catégorie définie</p>
              <p className="text-sm">Créez votre première catégorie d'événements</p>
            </div>
          ) : (
            categories.map(category => {
              const IconComponent = getIconComponent(category.icon);
              const isSystem = category.categoryType === 'SYSTEM';

              return (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${category.color}20` }}
                    >
                      <IconComponent size={20} style={{ color: category.color }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{category.name}</span>
                        {isSystem && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                            <Lock size={10} />
                            Système
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="font-mono text-xs">{category.code}</span>
                        <span>•</span>
                        <span>{VISIBILITY_LABELS[category.visibility]}</span>
                      </div>
                      {category.description && (
                        <p className="text-sm text-gray-500 mt-1">{category.description}</p>
                      )}
                    </div>
                  </div>

                  {canManage && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(category)}
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Pencil size={18} />
                      </button>
                      {!isSystem && (
                        <button
                          onClick={() => handleDeleteClick(category)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Info */}
      {!showForm && categories.length > 0 && (
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            Les catégories définissent le type d'événement et qui peut le voir.
            Les catégories système ne peuvent pas être supprimées.
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!categoryToDelete}
        title="Supprimer la catégorie"
        message={`Êtes-vous sûr de vouloir supprimer la catégorie "${categoryToDelete?.name}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};
